// Tidsbokning Phase A — MatchDetailModal
//
// Opens when an admin clicks a Seriespel (league_match) block in the schedule
// grid (`/manager/schedule`). Phase A scope is **read-only**:
//
//   - League name + round/serie + match-number
//   - Home vs away team labels (dual-identity team_members embed)
//   - Effective scheduled time (COALESCE(rescheduled_*, scheduled_*)) in
//     Swedish locale
//   - Court name + hall name
//   - Ball holder (dual-identity user-or-customer via ballHolderDisplay)
//   - Status (scheduled / rescheduled / completed)
//   - Reported result (if set)
//   - Linked booking_id with an internal link to BookingDetailModal (Phase C
//     extension; Phase A just shows the id)
//   - "Flytta match" CTA (Phase A: stubbed to a toast; Phase C: opens
//     RescheduleLeagueMatchDialog and runs admin_reschedule_league_match RPC)
//
// All copy in Swedish — matches existing modal conventions (BookingDetailModal,
// RecurringBlockDetailDialog, SubscriptionDetailDialog).

import { useEffect, useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { resolveBallHolderFromEmbed } from "@/lib/ballHolderDisplay";
import { pickFirstTwoPrimaryIdentities, type ResolvedTeamMember } from "@/lib/playerDisplay";
import { RescheduleLeagueMatchDialog, type MatchRescheduleSource } from "@/components/admin/RescheduleLeagueMatchDialog";

interface MatchDetailModalProps {
  matchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after a successful Phase-C reschedule so the parent can refresh data. */
  onRescheduled?: () => void;
  /** Tidsbokning Phase D — when a drag-drop opened this modal, auto-open the
   *  RescheduleLeagueMatchDialog with this pre-fill. null/undefined for the
   *  click-path (just show read-only detail). */
  initialRescheduleTarget?: { courtId: string; date: string; time: string } | null;
}

// Shape returned by the FK-embedded fetch below. Kept loose (`any`-ish) on the
// team_members member arrays because the array shape isn't worth tightening
// for a single modal — the resolver helpers narrow internally.
type MatchRow = {
  id: string;
  round: number;
  status: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  rescheduled_date: string | null;
  rescheduled_time: string | null;
  result: unknown;
  booking_id: string | null;
  reported_at: string | null;
  confirmed_at: string | null;
  ball_holder_user_id: string | null;
  ball_holder_customer_id: string | null;
  ball_holder_user: { full_name: string | null } | null;
  ball_holder_customer: { full_name: string | null } | null;
  league: {
    id: string;
    name: string;
    organization_id: string;
    match_duration_minutes: number | null;
  } | null;
  division: { id: string; name: string | null } | null;
  court: { id: string; name: string; halls: { name: string } | null } | null;
  home_team: HomeAwayTeam | null;
  away_team: HomeAwayTeam | null;
};

type HomeAwayTeam = {
  id: string;
  team_name: string | null;
  members: Array<{
    user_id: string | null;
    customer_id: string | null;
    role: "primary" | "reserve";
    joined_at: string;
    profile?: { full_name: string | null } | null;
    customer?: { full_name: string | null; email: string | null } | null;
  }> | null;
};

function formatTeamLabel(team: HomeAwayTeam | null): string {
  if (!team) return "?";
  if (team.team_name) return team.team_name;
  const [p1, p2] = pickFirstTwoPrimaryIdentities(team.members);
  return `${displayName(p1)}/${displayName(p2)}`;
}

function displayName(m: ResolvedTeamMember | null): string {
  if (!m || m.kind === "none") return "?";
  return m.displayName.split(" ")[0] ?? "?";
}

const STATUS_LABELS_SV: Record<string, string> = {
  scheduled: "Schemalagd",
  rescheduled: "Omschemalagd",
  completed: "Genomförd",
  reported: "Rapporterad",
  cancelled: "Inställd",
  walkover: "Walkover",
};

function statusBadge(status: string): { label: string; variant: "default" | "secondary" | "outline" | "destructive" } {
  const label = STATUS_LABELS_SV[status] ?? status;
  switch (status) {
    case "completed":
    case "reported":
      return { label, variant: "default" };
    case "rescheduled":
      return { label, variant: "secondary" };
    case "cancelled":
    case "walkover":
      return { label, variant: "destructive" };
    default:
      return { label, variant: "outline" };
  }
}

export function MatchDetailModal({ matchId, open, onOpenChange, onRescheduled, initialRescheduleTarget }: MatchDetailModalProps) {
  const [match, setMatch] = useState<MatchRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [courts, setCourts] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (!open || !matchId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: fetchErr } = await supabase
        .from("league_matches")
        .select(`
          id, round, status,
          scheduled_date, scheduled_time, rescheduled_date, rescheduled_time,
          result, booking_id, reported_at, confirmed_at,
          ball_holder_user_id, ball_holder_customer_id,
          ball_holder_user:profiles!league_matches_ball_holder_user_id_fkey(full_name),
          ball_holder_customer:customers!league_matches_ball_holder_customer_id_fkey(full_name),
          league:leagues!league_matches_league_id_fkey(id, name, organization_id, match_duration_minutes),
          division:divisions!league_matches_division_id_fkey(id, name),
          court:courts!league_matches_court_id_fkey(id, name, halls(name)),
          home_team:teams!league_matches_home_team_id_fkey(
            id, team_name,
            members:team_members(
              user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email)
            )
          ),
          away_team:teams!league_matches_away_team_id_fkey(
            id, team_name,
            members:team_members(
              user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name),
              customer:customers!team_members_customer_id_fkey(full_name, email)
            )
          )
        `)
        .eq("id", matchId)
        .single();
      if (cancelled) return;
      if (fetchErr || !data) {
        setError(fetchErr?.message ?? "Match hittades inte");
        setMatch(null);
      } else {
        setMatch(data as unknown as MatchRow);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, matchId]);

  // Load courts for the same org (needed by RescheduleLeagueMatchDialog).
  // Fetch once on open; cheap.
  useEffect(() => {
    if (!open || !match?.league?.organization_id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("courts")
        .select("id, name, halls!inner(organization_id)")
        .eq("halls.organization_id", match.league!.organization_id)
        .order("sort_order");
      if (cancelled) return;
      setCourts(((data ?? []) as Array<{ id: string; name: string }>).map((c) => ({ id: c.id, name: c.name })));
    })();
    return () => {
      cancelled = true;
    };
  }, [open, match?.league?.organization_id]);

  // Tidsbokning Phase D — auto-open reschedule dialog when triggered by a
  // drag-drop. The initialRescheduleTarget hint is what AdminScheduleGrid
  // computed from the drop position. We wait until match + courts are loaded
  // before opening so the dialog can build its source correctly.
  useEffect(() => {
    if (open && initialRescheduleTarget && match && courts.length > 0 && !rescheduleOpen) {
      setRescheduleOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialRescheduleTarget, match?.id, courts.length]);

  // Effective schedule (rescheduled_* wins per Sprint 8 hard-lock convention)
  const effDate = match?.rescheduled_date ?? match?.scheduled_date ?? null;
  const effTime = match?.scheduled_time ?? match?.scheduled_time ?? null;
  const effTimeActual = match?.rescheduled_time ?? match?.scheduled_time ?? null;
  const dateLabel = effDate ? format(new Date(`${effDate}T00:00:00`), "EEEE d MMM yyyy", { locale: sv }) : "—";
  const timeLabel = effTimeActual ? effTimeActual.slice(0, 5) : "—";
  // We compose effTime above as a holdover; reset to actual:
  void effTime;

  const ballHolder = match ? resolveBallHolderFromEmbed(match) : { kind: "none" as const };
  const statusInfo = match ? statusBadge(match.status) : { label: "—", variant: "outline" as const };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Match-detaljer</DialogTitle>
          <DialogDescription>
            {match?.league?.name ?? "Liga"}
            {match?.division?.name ? ` · ${match.division.name}` : ""}
            {typeof match?.round === "number" ? ` · Omgång ${match.round}` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-sm text-muted-foreground py-6 text-center">Laddar match-detaljer…</div>
        )}

        {error && !loading && (
          <div className="text-sm text-destructive py-6 text-center">{error}</div>
        )}

        {!loading && !error && match && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              {match.rescheduled_date && (
                <span className="text-[11px] text-muted-foreground">
                  Ursprungligen: {match.scheduled_date} kl {match.scheduled_time?.slice(0, 5)}
                </span>
              )}
            </div>

            <Separator />

            <div className="grid grid-cols-3 gap-y-1.5">
              <span className="text-muted-foreground">Datum</span>
              <span className="col-span-2 font-medium">{dateLabel}</span>

              <span className="text-muted-foreground">Tid</span>
              <span className="col-span-2 font-medium">
                {timeLabel}
                {match.league?.match_duration_minutes != null && timeLabel !== "—" ? ` (${match.league.match_duration_minutes} min)` : ""}
              </span>

              <span className="text-muted-foreground">Bana</span>
              <span className="col-span-2 font-medium">
                {match.court?.name ?? "—"}
                {match.court?.halls?.name ? ` · ${match.court.halls.name}` : ""}
              </span>

              <span className="text-muted-foreground">Hemmalag</span>
              <span className="col-span-2 font-medium">{formatTeamLabel(match.home_team)}</span>

              <span className="text-muted-foreground">Bortalag</span>
              <span className="col-span-2 font-medium">{formatTeamLabel(match.away_team)}</span>

              <span className="text-muted-foreground">Bollhållare</span>
              <span className="col-span-2 font-medium">
                {ballHolder.kind === "none" ? "—" : ballHolder.displayName}
              </span>

              {/* Bug B fix — hide "ingen länkad bokning" line when no
                  blocking booking exists. The 98 pre-Sprint-7-Part-2
                  league_matches in the 2026-05-04 batch had NULL booking_id
                  and surfaced this misleading row. After backfill the row
                  appears with the linked booking id; before backfill, the
                  whole row is suppressed so the modal doesn't advertise a
                  broken state. */}
              {match.booking_id ? (
                <>
                  <span className="text-muted-foreground">Bokning</span>
                  <span className="col-span-2 font-mono text-xs break-all">
                    {match.booking_id}
                  </span>
                </>
              ) : null}
            </div>

            {!!match.result && (
              <>
                <Separator />
                <div>
                  <div className="text-muted-foreground mb-1">Resultat</div>
                  <pre className="text-xs bg-muted/40 rounded p-2 overflow-x-auto">
                    {JSON.stringify(match.result, null, 2)}
                  </pre>
                </div>
              </>
            )}

            <Separator />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Stäng
              </Button>
              <Button
                onClick={() => setRescheduleOpen(true)}
                disabled={!match || !match.league || courts.length === 0}
              >
                Flytta match
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Phase C — Reschedule dialog. Renders alongside the main modal so
          the user can keep the detail view open behind it.
          Phase D — when initialRescheduleTarget is set (drag-drop entry), the
          dialog opens automatically (see useEffect above) with the drop slot
          pre-filled as TILL. */}
      {rescheduleOpen && match && match.league && (
        <RescheduleLeagueMatchDialog
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          source={buildRescheduleSource(match)}
          courts={courts}
          initialTarget={initialRescheduleTarget ?? null}
          adminId={undefined /* auth.uid() resolves server-side via RPC default */}
          onRescheduled={() => {
            setRescheduleOpen(false);
            onRescheduled?.();
            onOpenChange(false);
          }}
        />
      )}
    </Dialog>
  );
}

function buildRescheduleSource(m: MatchRow): MatchRescheduleSource {
  const effDate = m.rescheduled_date ?? m.scheduled_date;
  const effTime = m.rescheduled_time ?? m.scheduled_time;
  const leagueName = m.league?.name ?? "Liga";
  const round = typeof m.round === "number" ? ` · Omgång ${m.round}` : "";
  return {
    matchId: m.id,
    bookingId: m.booking_id,
    currentCourtId: m.court?.id ?? null,
    currentDate: effDate,
    currentTime: effTime,
    durationMinutes: m.league?.match_duration_minutes ?? 90,
    label: `${leagueName}${round}`,
    homeTeamLabel: m.home_team?.team_name ?? null,
    awayTeamLabel: m.away_team?.team_name ?? null,
  };
}
